import { Injectable } from '@angular/core';
import heic2any from 'heic2any';

const MAX_AVATAR_EDGE = 1024;
const AVATAR_PROCESSING_TYPE = 'image/jpeg';
const AVATAR_PROCESSING_EXTENSION = '.jpeg';
const AVATAR_PROCESSING_QUALITY = 0.9;

@Injectable({
  providedIn: 'root'
})
export class ImageProcessingService {

  constructor() { }

  public async prepareAvatarUpload(file: File): Promise<File> {
    let workingFile = file;

    if (this.isHeic(file)) {
      workingFile = await this.convertHeicToJpegFile(file);
    }

    // GIF animation would be lost on canvas, so for avatars that is usually OK.
    // If you want to forbid GIF instead, reject it here.

    const blob = await this.resizeImageToMaxEdge(
      workingFile
    );

    return new File(
      [blob],
      file.name.replace(/\.\w+$/, AVATAR_PROCESSING_EXTENSION),
      { type: AVATAR_PROCESSING_TYPE }
    );
  }

  public resizeImageToMaxEdge(
    file: File
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const maxEdge = MAX_AVATAR_EDGE;

      reader.onload = () => {
        const img = new Image();

        img.onload = () => {
          const originalWidth = img.width;
          const originalHeight = img.height;

          const longerEdge = Math.max(originalWidth, originalHeight);
          const scale = Math.min(maxEdge / longerEdge, 1); // prevents upscaling

          const targetWidth = Math.round(originalWidth * scale);
          const targetHeight = Math.round(originalHeight * scale);

          const canvas = document.createElement('canvas');
          canvas.width = targetWidth;
          canvas.height = targetHeight;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas context not available'));
            return;
          }

          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to create resized image'));
                return;
              }
              resolve(blob);
            },
            AVATAR_PROCESSING_TYPE,
            AVATAR_PROCESSING_QUALITY
          );
        };

        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = reader.result as string;
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  public resizeImageUrlToSquare(
    imageUrl: string,
    size: number
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        const originalWidth = img.width;
        const originalHeight = img.height;

        const cropSize = Math.min(originalWidth, originalHeight);
        const sx = Math.floor((originalWidth - cropSize) / 2);
        const sy = Math.floor((originalHeight - cropSize) / 2);

        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        ctx.drawImage(
          img,
          sx,
          sy,
          cropSize,
          cropSize,
          0,
          0,
          size,
          size
        );

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create output image'));
              return;
            }

            const objectUrl = URL.createObjectURL(blob);
            resolve(objectUrl);
          },
          AVATAR_PROCESSING_TYPE,
          AVATAR_PROCESSING_QUALITY
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));

      img.src = imageUrl;
    });
  }

  public isSupportedUploadImage(file: File): boolean {
    const allowedMimeTypes = [
      'image/png',
      'image/jpeg',
      'image/webp',
      'image/avif',
      'image/gif',
      'image/bmp',
      'image/heic',
      'image/heif'
    ];

    const lowerName = file.name.toLowerCase();

    return (
      allowedMimeTypes.includes(file.type) ||
      lowerName.endsWith('.png') ||
      lowerName.endsWith('.jpg') ||
      lowerName.endsWith('.jpeg') ||
      lowerName.endsWith('.webp') ||
      lowerName.endsWith('.avif') ||
      lowerName.endsWith('.gif') ||
      lowerName.endsWith('.bmp') ||
      lowerName.endsWith('.heic') ||
      lowerName.endsWith('.heif')
    );
  }

  public isHeic(file: File): boolean {
    const lowerName = file.name.toLowerCase();

    return (
      file.type === 'image/heic' ||
      file.type === 'image/heif' ||
      lowerName.endsWith('.heic') ||
      lowerName.endsWith('.heif')
    );
  }

  public async convertHeicToJpegFile(file: File): Promise<File> {
    const result = await heic2any({
      blob: file,
      toType: AVATAR_PROCESSING_TYPE,
      quality: AVATAR_PROCESSING_QUALITY
    });

    const blob = Array.isArray(result) ? result[0] : result;

    return new File(
      [blob as Blob],
      file.name.replace(/\.\w+$/, AVATAR_PROCESSING_EXTENSION),
      { type: AVATAR_PROCESSING_TYPE }
    );
  }
}
